import { Injectable, OnDestroy, inject } from '@angular/core';
import { UnassignedShift } from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import {
  debounceTime,
  filter,
  map,
  Observable,
  pairwise,
  shareReplay,
  Subscription,
  switchMap,
} from 'rxjs';
import { ShiftId } from '../interfaces';
import { ShiftTimelineUnassignedRegionStateFacadeService } from './shift-timeline-unassigned-region-state-facade.service';
import { ShiftTimelineFilterUnassignedRegionStateFacadeService } from '../shift-timeline-filter-unassigned-region/shift-timeline-filter-unassigned-region-state-facade.service';
import { Store } from '@ngxs/store';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import { AuthState } from '@wilson/auth/core';

@Injectable()
export class UnassignedRegionVisibleShiftsStateService implements OnDestroy {
  private readonly store = inject(Store);
  private readonly shiftTimelineUnassignedRegionStateFacadeService = inject(
    ShiftTimelineUnassignedRegionStateFacadeService,
  );
  private readonly shiftTimelineFilterUnassignedRegionStateFacadeService =
    inject(ShiftTimelineFilterUnassignedRegionStateFacadeService);

  @WilsonState(new Set())
  private visibleShiftIdsSet!: Set<ShiftId>;
  public visibleShiftIdsSet$!: Observable<Set<ShiftId>>;
  private autoCleanUpSubscription!: Subscription;

  public newlyVisibleShiftIdsSet$: Observable<{
    new: Set<ShiftId>;
    previouslyVisibleShiftIds: Set<ShiftId>;
  }> = this.visibleShiftIdsSet$.pipe(
    debounceTime(1),
    pairwise(),
    map(([prev, curr]) => {
      const result = {
        new: new Set<ShiftId>(),
        previouslyVisibleShiftIds: new Set<ShiftId>(),
      };
      curr.forEach((id) => {
        if (!prev.has(id)) {
          result.new.add(id);
        } else {
          result.previouslyVisibleShiftIds.add(id);
        }
      });
      return result;
    }),
    filter((result) => result.new.size > 0),
    shareReplay(1),
  );

  updateVisibility(
    visibleShiftEvents: {
      shiftId: string;
      isVisible: boolean;
    }[],
  ): void {
    const newSet: Set<string> = new Set(this.visibleShiftIdsSet);

    visibleShiftEvents.forEach((visibleShiftId) => {
      if (visibleShiftId.isVisible) {
        if (!this.visibleShiftIdsSet.has(visibleShiftId.shiftId)) {
          newSet.add(visibleShiftId.shiftId);
        }
      } else {
        if (this.visibleShiftIdsSet.has(visibleShiftId.shiftId)) {
          newSet.delete(visibleShiftId.shiftId);
        }
      }
    });

    this.visibleShiftIdsSet = newSet;
  }

  updateVisibilityForShifts(currentVisibleShiftIds: Set<ShiftId>): void {
    this.visibleShiftIdsSet = currentVisibleShiftIds;
  }

  clearVisibleShiftIdsSet(): void {
    this.visibleShiftIdsSet = new Set();
  }

  private isCellView$ = this.store.select(AuthState.userId).pipe(
    filter((userId) => userId !== null),
    switchMap((userId) =>
      this.store.select(ShiftTimelinePreferredSettingsState.isCellView(userId)),
    ),
  );

  private cellViewUnassignedShiftsMap$: Observable<
    Record<ShiftId, UnassignedShift>
  > =
    this.shiftTimelineUnassignedRegionStateFacadeService.filteredUnassignedShiftsStacksWithoutActivities$.pipe(
      map((stacks) => {
        return stacks.reduce(
          (acc, stack) => {
            Object.values(stack.shifts).forEach((shift) => {
              acc[shift.id] = shift;
            });
            return acc;
          },
          {} as Record<ShiftId, UnassignedShift>,
        );
      }),
      shareReplay(1),
    );

  private standardViewUnassignedShiftsMap$: Observable<
    Record<ShiftId, UnassignedShift>
  > =
    this.shiftTimelineFilterUnassignedRegionStateFacadeService.filteredUnassignedShiftsWithoutActivities$.pipe(
      map((stacks) => {
        return stacks.reduce(
          (acc, shift: UnassignedShift) => {
            acc[shift.id] = shift;
            return acc;
          },
          {} as Record<ShiftId, UnassignedShift>,
        );
      }),
      shareReplay(1),
    );

  constructor() {
    this.autoCleanUpSubscription = this.isCellView$
      .pipe(
        switchMap((isCellView) =>
          isCellView
            ? this.cellViewUnassignedShiftsMap$
            : this.standardViewUnassignedShiftsMap$,
        ),
        debounceTime(1),
      )
      .subscribe((unassignedShiftsMap) => {
        const newSet = new Set(this.visibleShiftIdsSet);
        this.visibleShiftIdsSet.forEach((shiftId) => {
          if (!unassignedShiftsMap[shiftId]) {
            newSet.delete(shiftId);
          }
        });

        this.visibleShiftIdsSet = newSet;
      });
  }

  ngOnDestroy(): void {
    this.autoCleanUpSubscription.unsubscribe();
  }
}
